import React, { useMemo, useState } from 'react';
import { ActionIcon, Box, Flex, Select, Text, TextInput } from '@mantine/core';
import {
  IconGripVertical,
  IconCheck,
  IconX,
  IconArrowMergeLeft,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateSectionCreationData } from 'src/company/templates/types';
import { TemplateSection } from 'src/data-capture/hooks/templates/useTemplates';

type Props<T extends TemplateSectionCreationData | TemplateSection> = {
  section: T & { isCompleted?: boolean };
  isSelected: boolean;
  onSelect: () => void;
  dragIndex?: number | null;
  itemIndex?: number;
  enableDragDrop?: boolean;
  onDragReorder?: (fromIndex: number, toIndex: number) => void;
  onDragEnterItem?: (index: number) => void;
  onDeleteSection?: (section: T) => void;
  onMergeSection?: (section: T) => void;
  onEditSectionHeader?: (section: T, newHeader: string) => void;
  onChangeSectionHeaderLevel?: (section: T, newLevel: number) => void;
};

export function SectionListItem<T extends TemplateSectionCreationData | TemplateSection>({
  section,
  isSelected,
  onSelect,
  dragIndex,
  itemIndex = 0,
  enableDragDrop = false,
  onDragReorder,
  onDragEnterItem,
  onDeleteSection,
  onEditSectionHeader,
  onMergeSection,
  onChangeSectionHeaderLevel,
}: Props<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const [newSectionHeader, setNewSectionHeader] = useState(section.sectionHeader);
  const [isHeadingSectionEditMode, setIsHeadingSectionEditMode] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isDragOver = dragIndex === itemIndex;

  const backgroundColor = useMemo(() => {
    if (isDragOver) return 'var(--mantine-color-brand-1)';
    if (section.isCompleted && isSelected) return 'var(--mantine-color-success-1)';
    if (section.isCompleted) return 'var(--mantine-color-success-0)';
    if (isSelected) return 'var(--mantine-color-brand-0)';
    return 'var(--mantine-color-gray-0)';
  }, [section.isCompleted, isSelected, isDragOver]);

  const borderColor = useMemo(() => {
    if (isDragOver) return 'var(--mantine-color-brand-4)';
    if (section.isCompleted && isSelected) return 'var(--mantine-color-success-3)';
    if (section.isCompleted) return 'var(--mantine-color-success-2)';
    if (isSelected) return 'var(--mantine-color-brand-2)';
    return 'var(--mantine-color-gray-2)';
  }, [section.isCompleted, isSelected, isDragOver]);

  const headingLevelOptions = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6].map(level => ({
        value: level.toString(),
        label: `H${level}`,
      })),
    [],
  );

  const handleDragStart = (e: React.DragEvent) => {
    if (!enableDragDrop) return;
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemIndex.toString());
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!enableDragDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragEnterItem?.(itemIndex);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!enableDragDrop) return;
    e.preventDefault();
    onDragEnterItem?.(itemIndex);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!enableDragDrop) return;
    e.preventDefault();
    const draggedIndexStr = e.dataTransfer.getData('text/plain');
    const draggedIndex = parseInt(draggedIndexStr, 10);

    if (!isNaN(draggedIndex) && draggedIndex !== itemIndex && onDragReorder) {
      onDragReorder(draggedIndex, itemIndex);
    }
  };

  const handleCancelEdit = () => {
    setIsHeadingSectionEditMode(false);
    setNewSectionHeader(section.sectionHeader);
  };

  const handleConfirmEdit = () => {
    setIsHeadingSectionEditMode(false);
    onEditSectionHeader?.(section, newSectionHeader);
  };

  const cursor = getCursor(enableDragDrop, isDragging);

  return (
    <Box
      onClick={onSelect}
      p="xs"
      draggable={enableDragDrop}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        borderRadius: '8px',
        backgroundColor,
        border: `1px solid ${borderColor}`,
        cursor,
        minWidth: 0,
        opacity: isDragging ? 0.5 : 1,
        transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.15s ease',
      }}
    >
      <Flex align="center" gap="xs" style={{ minWidth: 0 }}>
        <IconGripVertical
          size={16}
          color={enableDragDrop ? 'var(--mantine-color-gray-6)' : 'var(--mantine-color-gray-5)'}
          style={{
            flexShrink: 0,
            cursor: enableDragDrop ? 'grab' : 'default',
          }}
        />
        {isHeadingSectionEditMode ? (
          <TextInput
            variant="inline"
            value={newSectionHeader}
            flex={1}
            onChange={e => {
              e.preventDefault();
              setNewSectionHeader(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            ref={inputRef}
            bg={'white'}
          />
        ) : (
          <Text
            size="sm"
            fw={FONT_WEIGHTS.medium}
            c="gray.10"
            flex={1}
            truncate
            style={{ minWidth: 0 }}
          >
            {newSectionHeader}
          </Text>
        )}
        {onChangeSectionHeaderLevel && (
          <Box
            onPointerDown={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}
            style={{ flexShrink: 0 }}
          >
            <Select
              size="xs"
              w={64}
              value={section.sectionHeaderLevel?.toString() ?? null}
              data={headingLevelOptions}
              aria-label="Heading level"
              onChange={value => {
                if (!value) {
                  return;
                }
                const level = parseInt(value, 10);
                if (!Number.isNaN(level)) {
                  onChangeSectionHeaderLevel(section, level);
                }
              }}
            />
          </Box>
        )}
        {section.isCompleted && (
          <IconCheck size={16} color="var(--mantine-color-success-6)" style={{ flexShrink: 0 }} />
        )}
        {!section.isCompleted && (
          <Flex direction={'row'}>
            {isHeadingSectionEditMode ? (
              <>
                <ActionIcon variant={'subtle'} size={'xs'} onClick={handleConfirmEdit}>
                  <IconCheck size={20} color={'black'} />
                </ActionIcon>
                <ActionIcon variant={'subtle'} size={'xs'} onClick={handleCancelEdit}>
                  <IconX size={20} color={'black'} />
                </ActionIcon>
              </>
            ) : (
              <>
                {onMergeSection && (
                  <ActionIcon
                    variant={'subtle'}
                    size={'xs'}
                    onClick={() => onMergeSection(section)}
                  >
                    <IconArrowMergeLeft size={20} color={'black'} />
                  </ActionIcon>
                )}

                {onEditSectionHeader && (
                  <ActionIcon
                    variant={'subtle'}
                    size={'xs'}
                    onClick={() => {
                      setIsHeadingSectionEditMode(true);
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    <IconPencil size={20} color={'black'} />
                  </ActionIcon>
                )}
                {onDeleteSection && (
                  <ActionIcon
                    variant={'subtle'}
                    size={'xs'}
                    onClick={() => onDeleteSection(section)}
                  >
                    <IconTrash size={20} color={'black'} />
                  </ActionIcon>
                )}
              </>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}

function getCursor(enableDragDrop: boolean, isDragging: boolean): 'grabbing' | 'grab' | 'pointer' {
  if (!enableDragDrop) {
    return 'pointer';
  }
  return isDragging ? 'grabbing' : 'grab';
}
